import { LogEventNames } from "../interfaces/ITelemetryLogger";
import { LogError } from "../../utils/ErrorTelemetry";
import { ErrorStringFromException } from "../../utils/utils";

export interface CacheItem<T> {
  key: string;
  value: T;
  lastChecked: number;
  expiryDate: string;
}

export class IndexeddbCacheService<T> {
  private db: IDBDatabase | null = null;
  private readonly dbName: string;
  private readonly storeName: string;
  private readonly dbVersion: number = 1;
  private readonly CACHE_EXPIRY_MS: number;

  constructor(dbName: string, storeName: string, cacheExpiryMs: number = 60 * 60 * 1000) {
    this.dbName = dbName;
    this.storeName = storeName;
    this.CACHE_EXPIRY_MS = cacheExpiryMs;
  }

  public async initialize(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        const request: IDBOpenDBRequest = indexedDB.open(this.dbName, this.dbVersion);
        
        request.onerror = () => {
          const errorMessage = `Failed to open database ${this.dbName}. Error: ${request.error}`;
          LogError(LogEventNames.Warning, errorMessage);
          reject(new Error(errorMessage));
        };

        request.onsuccess = () => {
          this.db = request.result;
          resolve();
        };

        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains(this.storeName)) {
            db.createObjectStore(this.storeName, { keyPath: 'key' });
          }
        };
      } catch (e) {
        const errorMessage = `Failed to initialize database ${this.dbName}. Error: ${ErrorStringFromException(e)}`;
        LogError(LogEventNames.Warning, errorMessage);
        reject(new Error(errorMessage));
      }
    });
  }

  public async set(key: string, value: T): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    const cacheItem: CacheItem<T> = {
      key,
      value,
      lastChecked: Date.now(),
      expiryDate: new Date(Date.now() + this.CACHE_EXPIRY_MS).toISOString()
    };

    return new Promise<void>((resolve, reject) => {
      const transaction = this.db!.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.put(cacheItem);

      request.onerror = () => {
        reject(new Error(`Failed to cache item with key ${key}`));
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  public async get(key: string): Promise<T | null> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise<T | null>((resolve, reject) => {
      const transaction = this.db!.transaction(this.storeName, 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(key);

      request.onerror = () => {
        reject(new Error(`Failed to get cached item with key ${key}`));
      };

      request.onsuccess = () => {
        const cacheItem = request.result as CacheItem<T> | undefined;
        if (!cacheItem) {
          resolve(null);
          return;
        }

        const now = Date.now();
        if (now - cacheItem.lastChecked > this.CACHE_EXPIRY_MS) {
          resolve(null);
          return;
        }

        resolve(cacheItem.value);
      };
    });
  }

  public async delete(key: string): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise<void>((resolve, reject) => {
      const transaction = this.db!.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(key);

      request.onerror = () => {
        reject(new Error(`Failed to delete cached item with key ${key}`));
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  public async clear(): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise<void>((resolve, reject) => {
      const transaction = this.db!.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();

      request.onerror = () => {
        reject(new Error('Failed to clear cache'));
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  public close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
} 